<script setup lang="ts">
import Logo from '~/components/logos/Logo.vue'
import Isologo from '~/components/logos/IsoLogo.vue'
import LanguagesSwitching from '~/components/LanguagesSwitching.vue'
import { useResultStore } from '~/store/result'
import { useAuthStore } from '~/store/auth'
import { trackOnboarding } from '~/tracking/onboardingEvents.js'
// import { trackSignUp } from '~/tracking/signUpEvents'
import { storeToRefs } from 'pinia'

defineOptions({
	name: 'HeaderResult',
})

const route = useRoute()
const router = useRouter()
const resultStore = useResultStore()
const authStore = useAuthStore()
const { user, loggedIn } = storeToRefs(authStore)
const localePath = useLocalePath()
const { t: $t } = useI18n()

const mounted = ref(false)
const loading = ref<boolean>(false)

const items = computed(() => {
	if (user.value === null) return []
	const { email = '' } = user.value as { email?: string }
	return [
		[
			{
				label: email,
				slot: 'account',
				disabled: true,
			},
		],
		[
			{
				label: $t('Sign out'),
				icon: 'i-heroicons-arrow-left-on-rectangle',
				click: () => logout(),
			},
		],
	]
})

const isLoading = computed(() => (mounted.value ? resultStore.loading : false))

const openModalTour = () => {
	resultStore.SET_STATE_RESULT({
		type: 'showModalWelcome',
		value: true,
	})
}

const startTour = async () => {
	const resultsPath = localePath({ name: 'results' })
	const isHome = route.name?.toString().startsWith('index')
	/* Home page */
	if (isHome) {
		openModalTour()
		return
	}

	if (resultStore.loading === true) return
	trackOnboarding({
		game_name: resultStore.getLotterySelect?.name || '',
	})
	/* Others pages */
	if (route.path !== resultsPath) {
		await router.push({ path: resultsPath, query: route.query })
	}
	openModalTour()
}

// const sendEvent = () => {
// 	trackSignUp({ section: 'header-results' })
// }

const openModalSignUp = () => {
	authStore.SET_STATE_AUTH({
		type: 'showModalSignUp',
		value: true,
	})
}

const redirectToResults = async () => {
	const internalRoutes = [localePath('/results/scan')]

	const isInternal = internalRoutes.some((internalRoute) => route.path.startsWith(internalRoute))
	if (!isInternal) return

	await navigateTo({
		path: localePath('/results'),
		query: route.query,
		hash: route.hash,
		replace: true,
	})
}

const logout = async () => {
	loading.value = true
	console.log('Logging out...')
	await authStore.REMOVE_TOKEN()
	await redirectToResults()
	loading.value = false
}

const getInitialsName = (user: object | null) => {
	if (!user) return ''

	const { first_name } = user as { first_name?: string }
	if (!first_name) return ''

	const parts = first_name.trim().split(/\s+/).filter(Boolean)

	// Caso: nombre + apellido → "AP"
	if (parts.length >= 2) {
		return parts[0][0].toUpperCase() + parts[1][0].toUpperCase()
	}

	// Caso: un solo nombre → "AN"
	const name = parts[0].toUpperCase()
	return name.slice(0, Math.min(2, name.length))
}

onMounted(() => (mounted.value = true))
</script>

<template>
	<header class="header bg-primary-500 py-4">
		<div class="container">
			<div class="flex justify-between items-center">
				<div>
					<NuxtLink
						:to="localePath('/')"
						aria-label="Luckywise360 – Home Page"
					>
						<Logo class="w-52 hidden sm:flex" />
						<Isologo
							class="text-neutral-50 flex sm:hidden"
							:size="25"
						/>
					</NuxtLink>
				</div>
				<div class="flex items-center gap-3 sm:gap-4">
					<UButton
						v-if="route.path.includes('results')"
						icon="i-material-symbols:info-outline-rounded"
						color="white"
						variant="soft"
						size="xl"
						:ui="{
							rounded: 'rounded-full',
							icon: {
								size: { xl: 'size-6' },
							},
						}"
						class="text-white cursor-pointer flex p-0 hover:text-neutral-50"
						:class="{ 'cursor-not-allowed text-white/80': isLoading }"
						@click="startTour"
					/>
					<LanguagesSwitching class-rounded="rounded-full" />
					<div
						v-if="loading"
						class="flex items-center gap-2 animate-pulse"
					>
						<div class="size-10 rounded-full bg-white/[0.5]" />
						<div class="h-4 w-48"></div>
					</div>
					<template v-else>
						<template v-if="!mounted">
							<div class="h-10 w-20 rounded-full bg-primary-800 animate-pulse" />
						</template>
						<template v-else>
							<UDropdown
								v-if="loggedIn"
								mode="hover"
								:items="items"
							>
								<UButton
									color="primary"
									variant="solid"
									:label="getInitialsName(user)"
									class="text-sm font-semibold leading-5 text-white bg-primary-700 py-2 px-6 border border-primary-700 hover:bg-primary-800 hover:border-primary-800 transition-transform duration-300"
									:ui="{
										rounded: 'rounded-full',
									}"
								/>

								<template #account="{ item }">
									<div class="text-left">
										<p class="font-semibold">{{ $t('Signed in as') }}</p>
										<p class="font-medium text-gray-900 text-xs">
											{{ item.label }}
										</p>
									</div>
								</template>
								<template #item="{ item }">
									<span class="truncate font-medium">{{ item.label }}</span>
									<UIcon
										:name="item.icon"
										class="flex-shrink-0 h-4 w-4 text-gray-400 dark:text-gray-500 ms-auto"
									/>
								</template>
							</UDropdown>
							<UButton
								v-else
								color="primary"
								variant="solid"
								:label="$t('Join for free')"
								class="text-sm font-semibold leading-5 text-white bg-primary-700 py-2 px-6 border border-primary-700 hover:bg-primary-800 hover:border-primary-800 transition-transform duration-300"
								:ui="{
									rounded: 'rounded-full',
								}"
								@click="openModalSignUp"
							/>
						</template>
					</template>
				</div>
			</div>
		</div>
	</header>
</template>
